import * as React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import "./addComment.scss";
import { addComment } from "../../../../../../sevices/apiComment";
import { useResponsive } from "../../../../../../hooks/useResponsive";
import { useBoardUsers } from "../../../../../../utils/reactQuery/users";
import { removeHtmlTagsAndEntities } from "../../../../../../utils/helpers";
import { useEffect } from "react";
import AvatarDisplay from "./AvatarDisplay";
import CommentEditor from "./CommentEditor";
import useMutate from "../../../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../../../hooks/useBoardIdByKey";

export default React.memo( function BasicTextFields({ cardId }) {
  const [comment, setComment] = useState("");
  const { id } = useBoardIdByKey();
  const [searchParams] = useSearchParams();
  const { isLoading, data } = useBoardUsers(searchParams.get("brd_id") || id);
  const [showEditor, setShowEditor] = useState(false);
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.users.logInUser);
  const isMobile = useResponsive("down", "md");
  const firstName = user?.first_name[0];
  const lastName = user?.last_name[0];
  const name = `${firstName || ""}${lastName || ""}`;
  const tooltipName = `${user?.first_name || ""} ${user?.last_name || ""}`;
  const [mentionListVisible, setMentionListVisible] = useState(false);
  const [elementPos, setElementPos] = useState();
  const [cursorPosition, setCursorPosition] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const editorRef = useRef(null);
  const [selectedId, setSelectedId] = useState([]);
  const [isDisable, setIsDisable] = useState(true);
  const [selectedUserForTag, setSelectedUserForTag] = useState([]);

  const { mutate: addCommentmutate } = useMutate(addComment,[],
    ()=>{},
    () => {
      queryClient.invalidateQueries({
        queryKey: [`history`, String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: [`comment`, cardId],
      });
      queryClient.invalidateQueries({
        queryKey: [`feeds`, id],
      });
      queryClient.invalidateQueries({
        queryKey: [`commentBoard`, id],
      });
      setComment("");
      setIsDisable(true);
      setShowEditor(false);
    },
    ()=>{},
  );

  const handleCancel = useCallback(() => {
    setShowEditor(false);
  }, []);

  const handleSave = useCallback( (e) => {
    if (comment && !isDisable) {
      var helper = [];
      selectedId.map((ele) => {
        for (let i = 0; data?.data?.data?.responseData?.length > i; i++) {
          if (data?.data?.data?.responseData[i]?.user_id === ele) {
            if (
              comment
                .toLowerCase()
                .includes(
                  data?.data?.data?.responseData[i]?.username?.toLowerCase()
                )
            ) {
              helper?.push(ele);
            }
          }
        }
      });
      addCommentmutate({
        card_id: cardId,
        comment: comment,
        tagged_user: [...new Set(helper)]?.join(",") || null,
      });
      setShowEditor(false);
    }
  },[comment, isDisable, selectedId, data, addCommentmutate, cardId]);

  const anchorHelper = useMemo(() => document.getElementById("forAnchor"), []);

  React.useEffect(() => {
    setElementPos(anchorHelper?.getBoundingClientRect());
  }, [anchorHelper]);

  const handleMention = useCallback( (cursorPos, editor) => {
    const anchorEl = document.getElementById("forAnchor");
    setElementPos((ele) => ({
      top: ele?.top + anchorEl.getBoundingClientRect().top,
      left: ele?.left + anchorEl.getBoundingClientRect().left,
    }));
    setCursorPosition(cursorPos - 1);
    setAnchorEl(anchorEl);
    setMentionListVisible(true);
  },[]);

  const handleUserSelect = useCallback( (user) => {
    const quillEditor = editorRef?.current?.getEditor(); // Access the Quill editor
    setSelectedId([...selectedId, user?.user_id]);

    quillEditor.deleteText(cursorPosition, 1); // Remove '@'
    quillEditor.insertText(cursorPosition, `@${user?.username} `); // Insert user's name
    quillEditor.setSelection(cursorPosition + user?.username?.length + 1); // Move cursor

    setMentionListVisible(false); // Hide mention list
    if (data) setSelectedUserForTag(data?.data?.data?.responseData);
  },[cursorPosition]);

  const handleComment = useCallback( (e) => {
    const commentData = removeHtmlTagsAndEntities(
      editorRef?.current?.value
    )?.trim();
    if (commentData) {
      setComment(e);
      setIsDisable(false);
    } else {
      setIsDisable(true);
      setComment("");
    }
  },[]);

  useEffect(() => {
    if (data) setSelectedUserForTag(data?.data?.data?.responseData);
  }, [data]);

  const handleChange = useCallback( (e) => {
    const searchedText = e?.target?.value?.trim();
    const filteredUsers = data?.data?.data?.responseData?.filter((user) => {
      return user?.username
        ?.toLowerCase()
        ?.includes(searchedText?.toLowerCase());
    });
    setSelectedUserForTag(filteredUsers);
  },[data]);

  const handleMentionClose = useCallback(() => {
    const quillEditor = editorRef?.current?.getEditor();
    setMentionListVisible(false);
    quillEditor.setSelection(cursorPosition + 1);
  }, [cursorPosition]);

  const handleEditor = useCallback(() => {
    setShowEditor(true);
  }, []);

  return (
    <>
      <div className="d-flex add-comment">
        <div>
          <AvatarDisplay user={user} name={name} tooltipName={tooltipName} />
        </div>
        {showEditor && (
          <CommentEditor
            comment={comment}
            handleComment={handleComment}
            handleMention={handleMention}
            editorRef={editorRef}
            isMobile={isMobile}
            setElementPos={setElementPos}
            mentionListVisible={mentionListVisible}
            anchorEl={anchorEl}
            elementPos={elementPos}
            handleMentionClose={handleMentionClose}
            isLoading={isLoading}
            handleChange={handleChange}
            selectedUserForTag={selectedUserForTag}
            handleUserSelect={handleUserSelect}
            handleSave={handleSave}
            handleCancel={handleCancel}
            isDisable={isDisable}
          />
        )}
        {!showEditor && (
          <div
            className="add-comment-field"
            onClick={handleEditor}>
            <p>Add a comment...</p>
          </div>
        )}
      </div>
    </>
  );
})
